import { FunctionComponent, useCallback, useEffect, useState } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import config from '@config'
import { FeatureFlagsEntry } from '@config/items/abTest'
import { getAttributesforGrowthBook } from '@utils/growthBook'
import { getLocalStorageItem, setLocalStorageItem } from '@utils/localStorage'
import nanoid from '@utils/random'
import useCMPCookieCategories from '@hooks/useCMPCookieCategories'
import useExecuteOnClientNavigation from '@hooks/useExecuteOnClientNavigation'
import useIsGrowthbookDevModeEnabled from '@hooks/useIsGrowthbookDevModeEnabled'
import useSubscriptionStatus from '@hooks/useSubscriptionStatus'
import useTracking, { TrackingFnType } from '@hooks/useTracking'
import { User } from '@hooks/useUser'

import type {
  GrowthBook as GrowthBookType,
  TrackingCallback,
} from '@growthbook/growthbook-react'

const {
  backend: { hotModuleReloading },
  growthBook: {
    apiHost: growthBookApiHost,
    clientKey: growthBookClientKey,
    localStorageUuidKey: growthBookLocalStorageUuidKey,
  },
  abTest: { featureFlagsEntries, windowKey: abTestWindowKey },
} = config

type FeatureFlagsEntriesKeys = keyof typeof featureFlagsEntries

type GrowthBookFeatures = {
  [key: string]: unknown
}

const getSessionId = (): string => {
  let sessionId = getLocalStorageItem<string>(growthBookLocalStorageUuidKey)

  if (!sessionId) {
    sessionId = nanoid()
    setLocalStorageItem(growthBookLocalStorageUuidKey, sessionId)
  }

  return sessionId
}

const createGrowthbookInstance = async ({
  isGrowthbookDevModeEnabled,
  onExperimentView,
}: {
  isGrowthbookDevModeEnabled: boolean
  onExperimentView: ({
    experiment,
    result,
  }: {
    experiment: Parameters<TrackingCallback>[0]
    result: Parameters<TrackingCallback>[1]
  }) => void
}): Promise<GrowthBookType> => {
  const GrowthBookModule = await import('@growthbook/growthbook-react').then(
    (mod) => mod.GrowthBook,
  )

  return new GrowthBookModule({
    apiHost: growthBookApiHost,
    clientKey: growthBookClientKey,
    // There is a GrowthBook Chrome DevTools Extension that can help
    // you debug and test feature flags in development.
    // To avoid exposing all of internal feature flags and experiments
    // to users - It should be false in production in most cases.
    // But you can enable it for development through Feature Flags Dashboard.
    enableDevMode: hotModuleReloading || isGrowthbookDevModeEnabled,
    trackingCallback: (experiment, result) => {
      onExperimentView({ experiment, result })
    },
  })
}

const GrowthBookManager: FunctionComponent = () => {
  const queryClient = useQueryClient()
  const { subscriptionStatus } = useSubscriptionStatus()
  const { C0002: arePerformanceCookiesEnabled } = useCMPCookieCategories(
    'someCategoriesChanged',
    ['C0002'],
  )

  const isGrowthbookDevModeEnabled = useIsGrowthbookDevModeEnabled()

  const [growthBookInstance, setGrowthBookInstance] =
    useState<GrowthBookType | null>(null)

  const trackingCallbackHandler = useCallback<
    TrackingFnType<{
      experiment: Parameters<TrackingCallback>[0]
      result: Parameters<TrackingCallback>[1]
    }>
  >(
    ({ extraData }) => ({
      event: 'Viewed Experiment',
      experiment_id: extraData.experiment.key,
      variation_id: extraData.result.key,
    }),
    [],
  )

  const onExperimentView = useTracking(trackingCallbackHandler)

  const updateGrowthBookInstance = useCallback(
    async ({
      isGrowthbookDevModeEnabled,
      onExperimentView,
    }: {
      isGrowthbookDevModeEnabled: boolean
      onExperimentView: ({
        experiment,
        result,
      }: {
        experiment: Parameters<TrackingCallback>[0]
        result: Parameters<TrackingCallback>[1]
      }) => void
    }): Promise<void> => {
      const newInstance = await createGrowthbookInstance({
        isGrowthbookDevModeEnabled,
        onExperimentView,
      })

      setGrowthBookInstance((prevGrowthBookInstance) => {
        if (prevGrowthBookInstance) {
          prevGrowthBookInstance.destroy()
        }

        return newInstance
      })
    },
    [],
  )

  useEffect(() => {
    if (arePerformanceCookiesEnabled) {
      if (
        queryClient.getQueryData<boolean>([
          'growthbook-initial-instance-created',
        ])
      ) {
        return
      }

      queryClient.setQueryData<boolean>(
        ['growthbook-initial-instance-created'],
        true,
      )

      updateGrowthBookInstance({
        isGrowthbookDevModeEnabled,
        onExperimentView,
      })
    }
  }, [
    arePerformanceCookiesEnabled,
    growthBookInstance,
    isGrowthbookDevModeEnabled,
    onExperimentView,
    queryClient,
    updateGrowthBookInstance,
  ])

  useEffect(() => {
    if (
      !queryClient.getQueryData<boolean>([
        'growthbook-initial-instance-created',
      ])
    ) {
      return
    }

    updateGrowthBookInstance({
      isGrowthbookDevModeEnabled,
      onExperimentView,
    })
  }, [
    isGrowthbookDevModeEnabled,
    onExperimentView,
    queryClient,
    updateGrowthBookInstance,
  ])

  useEffect(() => {
    if (!growthBookInstance) {
      return
    }

    growthBookInstance.setRenderer(() => {
      // go through the feature flags which can be enabled in GrowthBook
      Object.keys(featureFlagsEntries)
        .filter(
          (featureFlag) =>
            (
              featureFlagsEntries[
                featureFlag as FeatureFlagsEntriesKeys
              ] as FeatureFlagsEntry
            )?.disableInGrowthbook !== true,
        )
        .forEach((featureFlag) => {
          // Check if feature flag exists in GrowthBook features
          if (growthBookInstance.getFeatures()?.hasOwnProperty(featureFlag)) {
            const featureValue = growthBookInstance.getFeatureValue(
              featureFlag,
              undefined,
            )

            window[abTestWindowKey][featureFlag] = featureValue
          }
        })

      queryClient.setQueryData<GrowthBookFeatures>(
        ['growthbook-features'],
        Object.fromEntries(
          Object.entries(growthBookInstance.getFeatures()).map(([key]) => [
            key,
            growthBookInstance.getFeatureValue(key, undefined),
          ]),
        ),
      )
      queryClient.invalidateQueries({ queryKey: ['growthbook-features'] })
    })
  }, [growthBookInstance, queryClient])

  useEffect(() => {
    if (!growthBookInstance) {
      return
    }

    growthBookInstance.init({
      // If the network request takes longer than this (in milliseconds) -
      // render the page without the feature flags.
      // This is useful for preventing performance
      // issues if the GrowthBook API is slow to respond.
      timeout: 1000,
      // The GrowthBook SDK supports streaming with Server-Sent Events (SSE).
      // When enabled, changes to features within GrowthBook will be streamed
      // to the SDK in realtime as they are published.
      // We dont need it for now so disable streaming to reduce load on the server.
      streaming: false,
    })
  }, [growthBookInstance])

  useEffect(() => {
    if (growthBookInstance && subscriptionStatus) {
      const userData = queryClient.getQueryData<User>(['user'])
      // Attributes about the current user and request.
      // These are used for two things: Feature targeting and
      // Assigning persistent variations in A/B tests (e.g. user id "123" always gets variation B).

      growthBookInstance
        .setAttributes({
          //! NOTE: "Id" is used as "Assignment Attribute" in experiments to assign variations.
          //! This value is required for GrowthBook to run experiments and cant be null.
          //! If the user is not logged in or without id, we use the "sessionId" for this attribute
          //! which is random anonymous hash, persisted in a cookie or local storage.
          id: userData?.sub || getSessionId(),
          ...getAttributesforGrowthBook(queryClient),
        })
        .finally(() => {
          queryClient.setQueryData(['growthbook-ready'], true)
          queryClient.invalidateQueries({
            queryKey: ['growthbook-ready'],
            exact: true,
          })
        })
    }
  }, [growthBookInstance, subscriptionStatus, queryClient])

  const updateGrowthBookURL = useCallback(() => {
    if (growthBookInstance) {
      growthBookInstance.setURL(window.location.href)
      growthBookInstance
        .updateAttributes(getAttributesforGrowthBook(queryClient))
        .finally(() => {
          queryClient.setQueryData(['growthbook-ready'], true)
          queryClient.invalidateQueries({
            queryKey: ['growthbook-ready'],
            exact: true,
          })
        })
    }
  }, [growthBookInstance, queryClient])

  // For the SPA we need to let the GrowthBook instance know when
  // the URL changes so the active experiments can update accordingly.
  useExecuteOnClientNavigation(updateGrowthBookURL)

  return null
}

export default GrowthBookManager
